import { add, assoc, assocPath, compose, findIndex, map, path, pathOr, propEq, reduce } from 'ramda'
import Awaitable from 'spiceutils/dist/Awaitable'
import { createBasket, submitBasket } from '../../utils/api/basket'

function markOfferLineAsBusyHandler({ commit, offerLineId }) {
  let busyLineResolver

  const promise = new Promise((resolve) => {
    busyLineResolver = resolve
  })
  promise.then(() => commit('UNMARK_OFFER_LINE_AS_BUSY', { offerLineId }))
  commit('MARK_OFFER_LINE_AS_BUSY', { offerLineId, promise })

  return busyLineResolver
}

/**
 * @param {FetchPath} fetchPath
 * @param {function} yodaFormatter
 * @param {function} loadContactBasket
 * @param {function} loadAdditionalBasketData
 * @param {function} createBasketLine
 * @return {Object}
 */
export default ({
  fetchPath,
  yodaFormatter,
  loadContactBasket,
  loadAdditionalBasketData,
  createBasketLine,
}) => ({
  async loadCurrentBasket({ commit, dispatch, state, rootState }) {
    if (state.loading) return state.loading

    const {
      isAuthenticated,
      user,
    } = rootState.auth

    if (!isAuthenticated || !user) {
      commit('navigate', { path: '/login' }, { root: true })

      dispatch('notifications/flash/create', {
        content: 'You must be logged in to view your basket',
        type: 'error',
      }, { root: true })

      return false
    }

    const awaitable = new Awaitable()
    commit('setLoading', awaitable)

    const {
      attributes: {
        id: contactId,
      },
    } = user.data

    const currentBasket = await loadContactBasket(contactId).catch(() => null)

    commit('setCurrentBasket', currentBasket)
    awaitable.resolve(currentBasket)
    commit('setLoading', false)

    return currentBasket
  },

  async loadAdditionalBasketData({ commit, state }) {
    const awaitable = new Awaitable()
    commit('setLoadingAdditionalData', awaitable)

    const { currentBasket } = state

    const basket = await loadAdditionalBasketData(currentBasket)

    commit('setCurrentBasket', basket)
    awaitable.resolve(basket)
    commit('setLoadingAdditionalData', false)

    return basket
  },

  /**
   * Creates a new basket for the user if one does not already exist.
   *
   * @param {function} dispatch
   * @param {Object} state
   * @param {string} currencyCode
   * @return {Promise}
   */
  async xhrCreateBasketIfNull({ dispatch, state }, { currencyCode }) {
    let { currentBasket } = state

    if (!currentBasket) {
      currentBasket = await dispatch('createBasket', { currencyCode })
    }

    return currentBasket
  },

  async createBasket({ commit, dispatch, rootState }, { currencyCode }) {
    const awaitable = new Awaitable()
    commit('setLoading', awaitable)

    const { id: contactIri } = rootState.auth.user.data

    const currentBasket = await createBasket({
      contactIri,
      currencyCode,
    }).catch((e) => {
      awaitable.reject(e)
      commit('setLoading', false)
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'Encountered an error whilst creating your basket. Please try again.',
      }, { root: true })

      throw e
    })

    commit('setCurrentBasket', currentBasket)
    awaitable.resolve(currentBasket)
    commit('setLoading', false)

    return currentBasket
  },

  /**
   * Reload the basket totals only.
   *
   * @param {Function} commit
   * @param {Function} dispatch
   * @param {Object} state
   * @param {Object} rootState
   * @returns {Promise<void>}
   */
  async xhrReloadBasketTotals({ commit, dispatch, state, rootState }) {
    if (state.basketReloadPending) {
      commit('SET_BASKET_RELOAD_QUEUED', true)
      return
    }

    commit('SET_BASKET_RELOAD_PENDING', true)

    const contactId = path(['auth', 'user', 'data', 'attributes', 'id'], rootState)
    const basket = await fetchPath.run(
      {
        path: `/yoda/api/contacts/${contactId}/current-basket`,
        jsonPath: ['content', 'data'],
      },
    ).then(yodaFormatter)

    commit('setTotalsOnCurrentBasket', basket.totals)
    commit('SET_BASKET_RELOAD_PENDING', false)

    if (state.basketReloadQueued) {
      commit('SET_BASKET_RELOAD_QUEUED', false)
      dispatch('xhrReloadBasketTotals')
    }
  },

  async submitCurrentBasket({ commit, dispatch, state, rootState }, { offerIds }) {
    if (!rootState.auth.isAuthenticated) {
      commit('navigate', { path: '/login' }, { root: true })

      dispatch('notifications/flash/create', {
        content: 'You must be logged in to submit your basket.',
        type: 'error',
      }, { root: true })

      return false
    }

    if (!state.currentBasket) {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'You do not have a basket pending submission.',
      }, { root: true })

      return false
    }

    const currentBasketId = String(state.currentBasket.id)
    let submissionId = null

    try {
      const response = await submitBasket(state.currentBasket, offerIds)
      submissionId = response.content.submission.id
    } catch (e) {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'Encountered an error whilst submitting your basket. Please try again.',
      }, { root: true })

      throw e
    }

    dispatch('notifications/flash/create', {
      type: 'success',
      content: 'Basket submission successful',
    }, { root: true })

    commit('setCurrentBasket', null)

    commit('navigate', { actionType: 'push', path: '/basket/submitted', query: { basket: currentBasketId, submission: submissionId } }, { root: true })
    return true
  },

  /**
   * Adds a basket line to the basket.
   *
   * @param {function} dispatch
   * @param {function} commit
   * @param {Object} state
   * @param {Object} offer
   * @param {Object} offerLine
   * @return {Promise}
   */
  async xhrAddBasketLine({ dispatch, commit, state }, { offer, offerLine }) {
    await dispatch('auth/verifyAuthentication', null, { root: true }).catch((e) => {
      throw e
    })

    const busyLineResolver = markOfferLineAsBusyHandler({ commit, offerLineId: offerLine.id })

    if (state.loading) {
      await state.loading
    }

    const currentBasket = await dispatch('xhrCreateBasketIfNull', { currencyCode: offer.currencyCode })
    if (currentBasket.basketLines.find((basketLine) => basketLine.offerLine === offerLine.iri)) {
      dispatch(
        'notifications/flash/create',
        { content: 'This product is already in your basket' },
        { root: true },
      )

      busyLineResolver()
      return null
    }

    const basketLine = await createBasketLine({
      basket: currentBasket.iri,
      offerLine: offerLine.iri,
      unitQuantity: offerLine.unitQuantity || 0,
    }).catch((e) => {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: `Encountered an error adding ${offerLine.name} to your basket. Please try again.`,
      }, { root: true })
      throw e
    })

    dispatch('notifications/flash/create', { type: 'success', content: 'Line added to basket' }, { root: true })
    await dispatch('basketLinePostCreate', { basketLines: [basketLine], offer, offerLines: [offerLine] })

    busyLineResolver()

    return basketLine
  },

  /**
   * Dispatch request to bulk add lines to the basket.
   *
   * @param {function} commit
   * @param {function} dispatch
   * @param {Object} state
   * @param {Object} offer
   * @param {Array} offerLines
   * @return {Promise}
   */
  async xhrBulkAddBasketLines({ commit, dispatch, state }, { offer, offerLines }) {
    await dispatch('auth/verifyAuthentication', null, { root: true }).catch((e) => {
      throw e
    })

    if (state.loading) {
      await state.loading
    }

    const busyLineResolvers = map((offerLine) => markOfferLineAsBusyHandler({ commit, offerLineId: offerLine.id }), offerLines)
    const currentBasket = await dispatch('xhrCreateBasketIfNull', { currencyCode: offer.currencyCode })
    const basketLines = await fetchPath.run(
      {
        method: 'POST',
        path: '/yoda/api/basket-lines/bulk',
        data: map((offerLine) => ({
          basket: currentBasket.iri,
          offerLine: offerLine.iri,
          unitQuantity: offerLine.unitQuantity || 0,
        }))(offerLines),
        jsonPath: ['content', 'data'],
        headers: { 'Preload-Offer': offer.id },
      },
    ).then(map(yodaFormatter)).catch((e) => {
      map((busyLineResolver) => busyLineResolver(), busyLineResolvers)
      dispatch(
        'notifications/flash/create',
        {
          type: 'error',
          content: 'Encountered an error adding products to your basket. Please try again',
        },
        { root: true },
      )
      throw e
    })

    await dispatch('notifications/flash/create', {
      type: 'success',
      content: 'Products successfully added to the basket.',
    }, { root: true })

    map((busyLineResolver) => busyLineResolver(), busyLineResolvers)

    await dispatch('basketLinePostCreate', { basketLines, offer, offerLines })

    return basketLines
  },

  /**
   * Action to be called after one or more basket lines have been added.
   *
   * @param {function} dispatch
   * @param {function} commit
   * @param {Object} rootState
   * @param {Array} basketLines
   * @param {Object} offer
   * @param {Array} offerLines
   *
   * @return {Promise}
   */
  async basketLinePostCreate({ dispatch, commit }, { basketLines, offer, offerLines }) {
    const sumUnitQuantity = compose(reduce(add, 0), map(pathOr(0, ['unitQuantity'])))

    await commit('addLinesToCurrentBasket', basketLines)
    await commit('addOfferLinesToCurrentBasket', { offer, offerLines })

    if (sumUnitQuantity(basketLines) > 0) {
      dispatch('xhrReloadBasketTotals')
    }
  },

  /**
   * Makes request to update a basket line on the server.
   *
   * @param {Function} dispatch
   * @param {Function} commit
   * @param {Object} basketLine
   * @param {Object} offerLine
   * @returns {Promise}
   */
  async xhrUpdateBasketLine({ dispatch, commit }, { basketLine, offerLine }) {
    await dispatch('auth/verifyAuthentication', null, { root: true }).catch((e) => {
      throw e
    })

    const busyLineResolver = markOfferLineAsBusyHandler({ commit, offerLineId: offerLine.id })

    const updatedBasketLine = await fetchPath.run({
      path: `/yoda/api/basket-lines/${basketLine.id}`,
      method: 'put',
      data: basketLine,
      jsonPath: ['content', 'data'],
    }).then(yodaFormatter)
    await commit('MERGE_LINE_INTO_BASKET', updatedBasketLine)

    busyLineResolver()

    await dispatch('xhrReloadBasketTotals')
  },

  /**
   * Issue a delete basket line request and remove the line from the current basket.
   *
   * @param {Function} commit
   * @param {Function} dispatch
   * @param {Object} basketLine
   * @param {Object} offerLine
   * @returns {Promise}
   */
  async xhrDeleteBasketLine({ commit, dispatch }, { basketLine, offerLine }) {
    await dispatch('auth/verifyAuthentication', null, { root: true })

    const busyLineResolver = markOfferLineAsBusyHandler({ commit, offerLineId: offerLine.id })

    await fetchPath.run({
      path: `/yoda/api/basket-lines/${basketLine.id}`,
      method: 'delete',
    })
    await commit('REMOVE_LINES_FROM_BASKET', [basketLine])
    dispatch('notifications/flash/create', { content: 'Line removed from basket', type: 'success' }, { root: true })

    busyLineResolver()

    await dispatch('xhrReloadBasketTotals')
  },

  /**
   * Issues request to bulk remove basket lines.
   *
   * @param {function} dispatch
   * @param {function} commit
   * @param {Array} basketLines
   * @return {Promise<void>}
   */
  async xhrBulkDeleteBasketLines({ dispatch, commit }, basketLines) {
    await fetchPath.run(
      {
        method: 'DELETE',
        path: '/yoda/api/basket-lines/bulk',
        parameters: { ids: map(path(['id']), basketLines) },
      },
    ).catch((e) => {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'Encountered an error removing products from your basket. Please try again.',
      }, { root: true })
      throw e
    })

    await commit('REMOVE_LINES_FROM_BASKET', basketLines)

    dispatch('notifications/flash/create', {
      type: 'success',
      content: 'Products successfully removed from basket.',
    }, { root: true })

    dispatch('xhrReloadBasketTotals')
  },

  /**
   * Updates the value of a basket's property.
   *
   * @param {Object} state
   * @param {Function} commit
   * @param {String} property
   * @param {*} value
   */
  updateBasketProperty({ state, commit }, { property, value }) {
    if (state.currentBasket !== null) {
      commit('setCurrentBasket', assoc(property, value, state.currentBasket))
    }
  },

  /**
   * Updates the value of a basket line's property.
   *
   * @param {Object} state
   * @param {Function} dispatch
   * @param {Object} basketLine
   * @param {String} property
   * @param {*} value
   */
  updateBasketLineProperty({ state, dispatch }, { basketLine, property, value }) {
    const basketLines = pathOr([], ['currentBasket', 'basketLines'], state)
    const index = findIndex(propEq('id', Number(basketLine.id)), basketLines)

    if (index !== -1) {
      dispatch('updateBasketProperty', {
        property: 'basketLines',
        value: assocPath([index, property], value, basketLines),
      })
    }
  },
})
